import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface AddListComposerProps {
  onAdd: (name: string) => void;
}

export default function AddListComposer({ onAdd }: AddListComposerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && ref.current) ref.current.focus();
  }, [open]);

  const submit = () => {
    const name = value.trim();
    if (!name) return;
    onAdd(name);
    setValue("");
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-fit w-72 shrink-0 items-center gap-1.5 rounded-xl bg-[var(--p-color-bg-surface-secondary)] px-3 py-2.5 text-left text-[13px] max-md:min-h-11 font-medium text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-tertiary)]"
      >
        <span className="text-base leading-none">+</span>
        {t("project-board-column-create", { defaultValue: "Add a list" })}
      </button>
    );
  }

  return (
    <div className="h-fit w-72 shrink-0 rounded-xl bg-[var(--p-color-bg-surface-secondary)] p-2 shadow-sm">
      <input
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
          if (e.key === "Escape") {
            setOpen(false);
            setValue("");
          }
        }}
        placeholder={t("project-board-column-create", { defaultValue: "Enter list title…" })}
        className="w-full rounded-md border border-[var(--p-color-border)] px-2.5 py-1.5 text-[16px] lg:text-[13px] focus:border-[var(--p-color-border-focus)] focus:outline-none focus:ring-1 focus:ring-[var(--p-color-border-focus)]"
      />
      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          className="rounded-md bg-[var(--p-color-bg-fill-brand)] px-3 py-1.5 text-[13px] font-medium max-md:min-h-11 text-[var(--p-color-text-brand-on-bg-fill)] hover:bg-[var(--p-color-bg-fill-brand-hover)]"
        >
          {t("save", { defaultValue: "Add list" })}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setValue("");
          }}
          className="rounded-md px-2 py-1.5 text-[13px] max-md:min-h-11 max-md:min-w-11 text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-tertiary)]"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
